import React from 'react';
import { useSelector } from 'react-redux';
import {
  Ad,
  AppContent,
  ArticleContent,
  ArticleMainImage,
  ArticleTitle,
  ContentAndSidebar,
  DecoratedQuote,
  Divider,
  ErrorBoundary,
  MainWrapperForDefaultNavigation,
  PostMainContent,
  RecommendationsAdvertisement,
  ShareButton,
  Sponsor,
  ArticleImage,
} from 'mm-ui-components';
import { Store } from 'redux';
import { Theme } from '../../components/connectedComponents/Theme';
import { PostMetaTags } from '../../components/connectedComponents/PostMetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { Page } from '../Page';
import { OldDefaultHeaderConnected } from '../../components/connectedComponents/HeaderConnected';
import { OldFooterConnected } from '../../components/connectedComponents/FooterConnected';
import {
  getAuthors,
  getBody,
  getBy,
  getCover,
  getShareConfig,
  getSponsor,
  getTitle,
  getUpdatedAtFormatted,
} from '../../store/template/postPage/postPage.selectors';
import { getAdSlotId, getCanonicalUrl, getSiteName, getTemplateName } from '../../store/config/config.selectors';
import { SLOT_NAMES } from '../templates.const';
import { PostSidebarConnected } from '../../components/connectedComponents/PostSidebarConnected';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import {
  getRevcontentWidgetID,
  getShowRecommendationsAdvertisement,
} from '../../store/template/postPageWithRecommendationsAdvertisement/postPageWithRecommendationsAdvertisement.selectors';
import { createDefaultBlocksWidths } from '../postPage.util';

interface PostPageProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

const blocksWithMultipleOptions = {
  divider: Divider,
  quote: DecoratedQuote,
  image: ArticleImage,
};

export const PostPageWithRecommendationsAdvertisementComponent: React.FunctionComponent<PostPageProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <PostPageWithRecommendationsAdvertisementContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

export const PostPageWithRecommendationsAdvertisementContentConnected: React.FunctionComponent = () => {
  const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
  const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
  const sideBar1Id = useSelector(getAdSlotId(SLOT_NAMES.SIDE_BAR_1));
  const sideBar2Id = useSelector(getAdSlotId(SLOT_NAMES.SIDE_BAR_2));
  const title = useSelector(getTitle);
  const by = useSelector(getBy);
  const authors = useSelector(getAuthors);
  const updatedAtFormatted = useSelector(getUpdatedAtFormatted);
  const cover = useSelector(getCover);
  const body = useSelector(getBody);
  const shareConfig = useSelector(getShareConfig);
  const canonical = useSelector(getCanonicalUrl);
  const templateName = useSelector(getTemplateName);
  const siteName = useSelector(getSiteName);
  const sponsor = useSelector(getSponsor);
  const showRecommendationsAdvertisement = useSelector(getShowRecommendationsAdvertisement);
  const revcontentWidgetID = useSelector(getRevcontentWidgetID);
  const defaultBlocksWidths = createDefaultBlocksWidths();

  return (
    <React.Fragment>
      <PostMetaTags />
      <HeadScripts />
      <Theme>
        <AppContent>
          <OldDefaultHeaderConnected />
          <MainWrapperForDefaultNavigation configThemeKey="mainArticleContent">
            <Ad id={topAdId} />
            <ContentAndSidebar>
              <PostMainContent>
                <header>
                  <ArticleTitle title={title} by={by} updatedAtFormatted={updatedAtFormatted} authors={authors}>
                    <ShareButton shareConfig={shareConfig} canonical={canonical} />
                  </ArticleTitle>
                  <ArticleMainImage {...cover} siteName={siteName} templateName={templateName} defaultBlocksWidths={defaultBlocksWidths} />
                </header>
                {sponsor ? <Sponsor {...sponsor} /> : null}
                <ArticleContent
                  blocksWithMultipleOptions={blocksWithMultipleOptions}
                  body={body}
                  templateName={templateName}
                  siteName={siteName}
                  defaultBlocksWidths={defaultBlocksWidths}
                />
              </PostMainContent>
              <PostSidebarConnected adId1={sideBar1Id} adId2={sideBar2Id} />
              {showRecommendationsAdvertisement
                ? <RecommendationsAdvertisement revcontentWidgetID={revcontentWidgetID} /> : null}
            </ContentAndSidebar>
            <Ad id={oneOnOneId} />
          </MainWrapperForDefaultNavigation>
          <OldFooterConnected />
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};
